import { compare, isNegative, isPositive, subtract, zero, } from '@aeris/money';
export const exchangeEligibility = (origin) => {
    const sold = (origin.kind === 'sale' && origin.status === 'completed') ||
        (origin.kind === 'order' && origin.status === 'fulfilled');
    if (!sold)
        return { ok: false, refusal: { reason: 'sale-not-completed' } };
    if (origin.customerIsSystem) {
        return { ok: false, refusal: { reason: 'walk-in-customer' } };
    }
    if (origin.hasOpenDeliveryReceivable) {
        return { ok: false, refusal: { reason: 'delivery-not-settled' } };
    }
    return { ok: true };
};
/** Quanto desta linha ainda pode voltar. Nunca negativo. */
export const returnableQuantity = (sold, alreadyReturned) => {
    const left = subtract(sold, alreadyReturned);
    return isNegative(left) ? zero(left.scale) : left;
};
/**
 * O motivo que vale para a peça: o dela, ou o da troca (F178).
 *
 * Escrito uma vez porque são três leitores — a trava do estoque, o que fica
 * gravado na linha e o relatório — e dois deles longe daqui.
 */
export const reasonForLine = (header, line) => line ?? header;
/**
 * A peça devolvida volta ao estoque? Pelo motivo (F175, decisão do dono).
 *
 * A F145 tratava tudo o que volta pelo trocador como defeito, e a Elite
 * mostrou o caso que a regra não cobria: a peça de cor errada está boa, e o
 * vale a deixava fora do estoque para sempre. O motivo já era perguntado em
 * toda troca — agora ele decide:
 *
 * - **defeito**: não volta, é baixa;
 * - **tamanho ou cor, não serviu, arrependimento**: a peça não tem nada, volta;
 * - **outro**: a pessoa diz, peça por peça (`requested`).
 *
 * Quem decide é o servidor, e não a tela: um PC antigo que manda sempre
 * `false` também devolve ao estoque a peça de cor errada.
 */
export const restocksFor = (reasonCode, requested) => {
    switch (reasonCode) {
        case 'defeito':
            return false;
        case 'tamanho_ou_cor':
        case 'nao_serviu':
        case 'arrependimento':
            return true;
        case 'outro':
            return requested;
    }
};
export const checkReturn = (input) => {
    /*
     * A mesma função que o pedido de troca usa, e que a tela lê pelo servidor.
     *
     * Antes, esta checagem era um `saleStatus !== 'completed'` alimentado por um
     * truque na camada de baixo (`kind === 'sale' ? status : 'not-a-sale'`), que
     * escondia metade da regra num ternário a três arquivos de distância.
     */
    const eligible = exchangeEligibility(input.origin);
    if (!eligible.ok)
        return eligible;
    if (input.lines.length === 0) {
        return { ok: false, refusal: { reason: 'nothing-returned' } };
    }
    const seen = new Set();
    for (const line of input.lines) {
        if (!input.saleLineIds.has(line.saleLineId)) {
            return {
                ok: false,
                refusal: { reason: 'line-not-in-sale', saleLineId: line.saleLineId },
            };
        }
        // Duas linhas para a mesma peça burlariam a soma: cada uma sozinha cabe no
        // que resta, as duas juntas não.
        if (seen.has(line.saleLineId)) {
            return {
                ok: false,
                refusal: { reason: 'duplicate-line', saleLineId: line.saleLineId },
            };
        }
        seen.add(line.saleLineId);
        // O motivo da peça, com o da troca como padrão (F178).
        if (line.restock &&
            reasonForLine(input.reasonCode, line.reasonCode) === 'defeito') {
            return {
                ok: false,
                refusal: { reason: 'restock-not-allowed', saleLineId: line.saleLineId },
            };
        }
        if (!isPositive(line.quantity)) {
            return {
                ok: false,
                refusal: {
                    reason: 'quantity-not-positive',
                    saleLineId: line.saleLineId,
                },
            };
        }
        const returnable = returnableQuantity(line.sold, line.alreadyReturned);
        /*
         * O teto da peça liberada pelo gerente: o vendido, menos o que virou vale
         * (F257). O que voltou numa troca pode voltar de novo; o vale, não.
         */
        const asVoucher = line.returnedAsVoucher ?? zero(line.sold.scale);
        const repeatable = returnableQuantity(line.sold, asVoucher);
        /*
         * A peça que já voltou inteira (F178).
         *
         * Vem ANTES de `over-returned` e é o caso que a Elite vive: a tela de uma
         * venda de uma tela só, trocada na terça, aparecendo devolvível de novo na
         * quinta. O servidor já recusava — de `over-returned`, com a frase da
         * quantidade. Agora ela tem nome próprio, diz o quanto já voltou, e é a
         * única recusa que a senha do gerente atravessa.
         */
        if (isPositive(line.alreadyReturned) && !isPositive(returnable)) {
            /*
             * Tudo o que voltou virou vale: nem o gerente reabre (F257). Vem antes
             * da recusa que a senha atravessa, para a tela não pedir uma senha que
             * não adiantaria.
             */
            if (!isPositive(repeatable)) {
                return {
                    ok: false,
                    refusal: {
                        reason: 'returned-as-voucher',
                        saleLineId: line.saleLineId,
                        returnedAsVoucher: asVoucher,
                        sold: line.sold,
                    },
                };
            }
        }
        if (line.allowRepeat !== true &&
            isPositive(line.alreadyReturned) &&
            !isPositive(returnable)) {
            return {
                ok: false,
                refusal: {
                    reason: 'already-returned',
                    saleLineId: line.saleLineId,
                    alreadyReturned: line.alreadyReturned,
                    sold: line.sold,
                },
            };
        }
        // Liberada pelo gerente, o que voltou numa troca deixa de contar; o que
        // virou vale continua contando (F257). Sem vale, o teto é o vendido.
        if (line.allowRepeat === true) {
            if (compare(line.quantity, repeatable) > 0) {
                return {
                    ok: false,
                    refusal: {
                        reason: 'over-returned',
                        saleLineId: line.saleLineId,
                        returnable: repeatable,
                    },
                };
            }
            continue;
        }
        if (compare(line.quantity, returnable) > 0) {
            return {
                ok: false,
                refusal: {
                    reason: 'over-returned',
                    saleLineId: line.saleLineId,
                    returnable,
                },
            };
        }
    }
    /*
     * Com mais de uma peça, cada uma diz o seu motivo — F188.
     *
     * Segunda passada, e de propósito: peça repetida e peça que não é da venda
     * são erros de ESTRUTURA, e quem errou a estrutura precisa ouvir isso antes
     * de ouvir que falta preencher um campo. A ordem inversa mandava o trocador
     * escolher o motivo de uma linha que nem devia estar ali.
     *
     * A checagem é da LISTA, não da linha: é o número de peças que decide se
     * herdar o motivo do cabeçalho ainda quer dizer alguma coisa.
     */
    if (input.lines.length > 1) {
        const semMotivo = input.lines.find((line) => line.reasonCode === undefined);
        if (semMotivo !== undefined) {
            return {
                ok: false,
                refusal: {
                    reason: 'line-reason-required',
                    saleLineId: semMotivo.saleLineId,
                },
            };
        }
    }
    return { ok: true };
};
/** O nome digitado como identidade: sem espaço sobrando e sem caixa. */
export const typedPieceKey = (description) => description.trim().replace(/\s+/g, ' ').toLocaleLowerCase('pt-BR');
/** A chave da peça, ou nulo quando ela não diz qual é. */
const pieceKey = (piece) => {
    const typed = piece.description === null || piece.description.trim() === ''
        ? null
        : typedPieceKey(piece.description);
    if (piece.variantId !== null) {
        return typed === null ? `produto:${piece.variantId}` : null;
    }
    return typed === null ? null : `nome:${typed}`;
};
export const checkReturnWithoutSale = (input) => {
    if (input.customerIsSystem) {
        return { ok: false, refusal: { reason: 'walk-in-customer' } };
    }
    if (input.lines.length === 0) {
        return { ok: false, refusal: { reason: 'nothing-returned' } };
    }
    const seen = new Set();
    for (const line of input.lines) {
        const piece = { variantId: line.variantId, description: line.description };
        const key = pieceKey(line);
        if (key === null) {
            return { ok: false, refusal: { reason: 'outside-piece', ...piece } };
        }
        if (seen.has(key)) {
            return { ok: false, refusal: { reason: 'outside-duplicate', ...piece } };
        }
        seen.add(key);
        if (line.restock) {
            return { ok: false, refusal: { reason: 'outside-restock', ...piece } };
        }
        if (!isPositive(line.quantity)) {
            return { ok: false, refusal: { reason: 'outside-quantity', ...piece } };
        }
        if (!isPositive(line.unitValue)) {
            return { ok: false, refusal: { reason: 'outside-value', ...piece } };
        }
    }
    return { ok: true };
};
/**
 * O pedido é EXATAMENTE o que foi aprovado? (F148)
 *
 * A senha aprova um combinado, não um botão. Sem esta conferência o trocador
 * pediria a senha para um fone de R$ 30,00 e mandaria R$ 300,00 — e o registro
 * diria que o gerente autorizou. A ordem das peças não importa; o cliente, o
 * tipo, cada peça, cada quantidade e cada valor, sim.
 */
export const matchesAuthorisedReturn = (authorised, requested) => {
    if (authorised.customerPartyId !== requested.customerPartyId)
        return false;
    if (authorised.kind !== requested.kind)
        return false;
    if (authorised.lines.length !== requested.lines.length)
        return false;
    // Pela chave da peça: o produto, ou o nome digitado sem caixa nem espaços.
    const keyed = (lines) => lines
        .map((line) => ({ line, key: pieceKey(line) ?? '' }))
        .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    const expected = keyed(authorised.lines);
    const received = keyed(requested.lines);
    return expected.every(({ line, key }, index) => {
        const other = received[index];
        return (key !== '' &&
            other?.key === key &&
            compare(other.line.quantity, line.quantity) === 0 &&
            compare(other.line.unitValue, line.unitValue) === 0);
    });
};
//# sourceMappingURL=rules.js.map